import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, DollarSign } from 'lucide-react';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { toast } from 'sonner';

export const CustomerPricesManager = forwardRef(({ customerId }, ref) => {
  const [prices, setPrices] = useState([]);
  const [items, setItems] = useState([]);
  const [newPriceLine, setNewPriceLine] = useState({ item_id: '', unit_price_chf: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef();

  useImperativeHandle(ref, () => ({
    addPrice: () => {
      setShowForm(true);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }));

  useEffect(() => {
    loadPrices();
    loadItems();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrices = async () => {
    try {
      const response = await api.getCustomerPrices(customerId);
      setPrices(response.data);
    } catch (error) {
      console.error('Failed to load customer prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await api.getItems(true);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to load items:', error);
    }
  };

  const handleAddPrice = async () => {
    if (!newPriceLine.item_id || !newPriceLine.unit_price_chf) {
      toast.error('Seleziona articolo e prezzo');
      return;
    }

    try {
      const response = await api.createCustomerPrice(customerId, {
        item_id: newPriceLine.item_id,
        unit_price_chf: newPriceLine.unit_price_chf,
        notes: newPriceLine.notes || null
      });
      
      // Check if it was an update or new
      const existingIndex = prices.findIndex(p => p.item_id === newPriceLine.item_id);
      if (existingIndex >= 0) {
        const updatedPrices = [...prices];
        updatedPrices[existingIndex] = response.data;
        setPrices(updatedPrices);
      } else {
        setPrices([...prices, response.data]);
      }
      
      setNewPriceLine({ item_id: '', unit_price_chf: '', notes: '' });
      toast.success('Prezzo salvato');
    } catch (error) {
      toast.error('Errore nel salvataggio prezzo');
    }
  };

  const handleDeletePrice = async (priceId) => {
    try {
      await api.deleteCustomerPrice(customerId, priceId);
      setPrices(prices.filter(p => p.id !== priceId));
      toast.success('Prezzo eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.name} (${item.unit})` : itemId;
  };

  const getItemStandardPrice = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item?.standard_price_chf;
  };

  if (loading) {
    return <div className="p-4">Caricamento prezzi...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign size={20} className="text-slate-600" />
        <p className="text-slate-600">
          I prezzi specifici per questo cliente hanno priorità sui prezzi standard degli articoli.
        </p>
      </div>

      {/* Existing Prices */}
      {prices.length > 0 ? (
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-slate-700 mb-3">Prezzi configurati ({prices.length})</h4>
          {prices.map(price => (
            <div key={price.id} className="p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <span className="font-medium">{getItemName(price.item_id)}</span>
                  {getItemStandardPrice(price.item_id) && (
                    <span className="ml-3 text-sm text-slate-400 line-through">
                      {formatCurrency(getItemStandardPrice(price.item_id))}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono font-semibold text-lg text-primary">
                    {formatCurrency(price.unit_price_chf)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePrice(price.id)}
                    className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                    data-testid={`delete-price-${price.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              {price.notes && (
                <p className="text-sm text-slate-500 mt-2 italic">{price.notes}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
          <DollarSign size={40} className="mx-auto mb-3 opacity-40" />
          <p>Nessun prezzo specifico configurato</p>
          <p className="text-sm mt-1">Verranno applicati i prezzi standard degli articoli</p>
        </div>
      )}

      {/* Add Price Form */}
      {showForm && (
      <div className="border-t pt-6" ref={formRef}>
        <h4 className="font-medium text-sm text-slate-700 mb-4">Aggiungi prezzo specifico</h4>
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs text-slate-500">Articolo</Label>
              <Select
                value={newPriceLine.item_id}
                onValueChange={(value) => setNewPriceLine(prev => ({ ...prev, item_id: value }))}
              >
                <SelectTrigger data-testid="select-item">
                  <SelectValue placeholder="Seleziona articolo..." />
                </SelectTrigger>
                <SelectContent>
                  {items.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      <span>{item.name} ({item.unit})</span>
                      <span className="text-xs text-slate-400 ml-2">Standard: {formatCurrency(item.standard_price_chf)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500">Prezzo CHF</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newPriceLine.unit_price_chf}
                onChange={(e) => setNewPriceLine(prev => ({ 
                  ...prev, 
                  unit_price_chf: parseFloat(e.target.value) || '' 
                }))}
                data-testid="price-input"
              />
            </div>
            <Button onClick={handleAddPrice} data-testid="add-price-btn">
              <Plus size={16} className="mr-2" />
              Aggiungi
            </Button>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Note (opzionale)</Label>
            <Input
              placeholder="es. Sconto volume, accordo speciale..."
              value={newPriceLine.notes}
              onChange={(e) => setNewPriceLine(prev => ({ ...prev, notes: e.target.value }))}
              data-testid="notes-input"
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
});
